<!-- src/components/Applications/AfsalSereign/Steps/Step2.vue -->
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

import DatePicker from '@/components/Applications/Shared/DatePicker.vue'
import SSNInput from '../../Shared/SSNInput.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
  changeSteps?: () => void
}>()

// Validation rules
const rules = {
  umsokn: {
    rightHolderName: { required },
    rightHolderSSN: { required },
    rightHolderPassDate: { required },
  },
}
const v$ = useVuelidate(rules, { umsokn })

function onChange() {
  props.changeSteps?.()
}

// Expose for wrapper
async function validate(): Promise<boolean> {
  await v$.value.$validate()
  return !v$.value.$error
}
defineExpose({ validate })
</script>

<template>
  <div v-if="umsokn">
    <v-col class="!props.disable ? 'mx-auto' : ''">
      <v-row>
        <v-col>
          <h2 class="forms__header">
            {{ t('umsokn_afsal_sereign_step2_header1') }}
          </h2>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.rightHolderName"
            :readonly="props.disable"
            :label="t('umsokn_afsal_sereign_step2_nafn')"
            :placeholder="t('umsokn_afsal_sereign_step2_nafn')"
            variant="outlined"
            density="comfortable"
            type="text"
            @update:model-value="onChange"
          />
          <div class="error__text" v-if="v$.umsokn.rightHolderName.$error">
            {{ t('umsokn_umsaekjandi_nafn_error') }}
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <SSNInput
            v-model="umsokn.rightHolderSSN"
            :readonly="props.disable"
            :label="t('umsokn_afsal_sereign_step2_kennitala')"
            :placeholder="t('umsokn_afsal_sereign_step2_kennitala')"
            variant="outlined"
            density="comfortable"
            type="text"
            inputmode="numeric"
            pattern="[0-9\-]*"
            @update:model-value="onChange"
          />
          <div class="error__text" v-if="v$.umsokn.rightHolderSSN.$error">
            {{ t('umsokn_umsaekjandi_kennitala_error') }}
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <!-- 🗓 Shared datepicker -->
          <DatePicker
            v-model="umsokn.rightHolderPassDate"
            :disabled="props.disable"
            :label="t('umsokn_afsal_sereign_step2_danardagur')"
            :placeholder="t('umsokn_afsal_sereign_step2_velja_danardag')"
            @update:model-value="onChange"
          />
          <div class="error__text" v-if="v$.umsokn.rightHolderPassDate.$error">
            {{ t('common.val_required') }}
          </div>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.m-b-40 { margin-bottom: 40px; }
.forms__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}
.error__text {
  color: #b00020; /* Vuetify default error color */
  font-size: 0.875rem;
  margin-top: 4px;
}
</style>
